import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useTariffLayer } from "@/store/mode";
import { useKuperModalStore } from "@/store/useKuperModalStore";
import { useTariffSelectorUiStore } from "@/store/tariffSelectorUi";
import {
  CategoryGroup,
  isEdaTariffMode,
  isKuperTariffMode,
  TariffMode,
} from "@/types/base";
import { showNotification } from "@/utils/network";
import { EatsIcon, KuperIcon, YandexTaxiIcon } from "./icons";
import { ServiceOption } from "./types";

const SERVICE_ORDER: CategoryGroup[] = ["eda", "kuper", "taxi"];
const DEFAULT_TARIFF_BY_SERVICE: Record<CategoryGroup, TariffMode> = {
  taxi: "t_econom",
  eda: "eda_car",
  kuper: "kuper",
};

const tariffBelongsToService = (tariff: TariffMode, service: CategoryGroup) => {
  if (service === "taxi") return tariff.startsWith("t_");
  if (service === "kuper") return isKuperTariffMode(tariff);
  return isEdaTariffMode(tariff);
};

export const useServiceSelector = () => {
  const { t } = useTranslation();
  const {
    tariffLayer,
    serviceLayer,
    setTariffLayer,
    setServiceLayer,
  } = useTariffLayer();
  const isServiceMenuOpen = useTariffSelectorUiStore(
    (state) => state.isServiceMenuOpen
  );
  const toggleServiceMenu = useTariffSelectorUiStore(
    (state) => state.toggleServiceMenu
  );
  const closeServiceMenu = useTariffSelectorUiStore(
    (state) => state.closeServiceMenu
  );
  const wasKuperModalShown = useKuperModalStore(
    (state) => state.wasKuperModalShown
  );

  const services: Record<CategoryGroup, ServiceOption> = {
    eda: { id: "eda", label: t("common.eats"), Icon: EatsIcon },
    kuper: { id: "kuper", label: t("common.kuper"), Icon: KuperIcon },
    taxi: { id: "taxi", label: t("common.taxi"), Icon: YandexTaxiIcon },
  };

  const selectService = (service: CategoryGroup) => {
    closeServiceMenu();
    setServiceLayer(service);
    setTariffLayer(DEFAULT_TARIFF_BY_SERVICE[service]);

    if (service === "eda") {
      showNotification({ type: "success", text: t("tariffs.switchedToEats") });
      return;
    }

    if (service === "kuper") {
      showNotification({ type: "success", text: t("tariffs.switchedToKuper") });
      return;
    }

    showNotification({ type: "success", text: t("tariffs.switchedToTaxi") });
  };

  useEffect(() => {
    if (tariffBelongsToService(tariffLayer, serviceLayer)) return;
    setTariffLayer(DEFAULT_TARIFF_BY_SERVICE[serviceLayer]);
  }, [serviceLayer, tariffLayer, setTariffLayer]);

  useEffect(() => () => closeServiceMenu(), [closeServiceMenu]);

  return {
    currentService: services[serviceLayer],
    availableServices: SERVICE_ORDER.filter(
      (service) => service !== serviceLayer
    ).map((service) => services[service]),
    open: isServiceMenuOpen,
    highlightKuper: !wasKuperModalShown,
    toggle: toggleServiceMenu,
    select: selectService,
  };
};
